import {useCallback, useEffect, useRef, useState} from 'react';

import {isAllowedLoxoPodOrigin, normalizeLoxoPodUrl} from './podUrl';
import {exchangeAuthTokens} from './requests/exchangeAuthTokens';
import {useRoamingSettings} from './RoamingSettingsContext';
import {reportOutlookAddIn} from './rollbar';

const LOXO_LOGIN_BASE = 'https://app.loxo.co/login?client=office_add_in';

type AuthCompleteMessage = {
  type: 'authComplete';
  pod_url: string;
  exchange_code: string;
};

type AuthErrorMessage = {
  type: 'authError';
  error?: string;
};

type AuthDialogMessage = AuthCompleteMessage | AuthErrorMessage;

type DialogMessageReceivedArg =
  | {message: string; origin?: string}
  | {error: number};

const dialogMessageArgToString = (
  arg: DialogMessageReceivedArg
): string | null => {
  if (!('message' in arg) || typeof arg.message !== 'string') {
    return null;
  }
  return arg.message;
};

const parseAuthDialogMessage = (raw: string): AuthDialogMessage | null => {
  try {
    const message = JSON.parse(raw) as AuthDialogMessage;
    if (message.type === 'authComplete') {
      if (
        typeof message.pod_url !== 'string' ||
        typeof message.exchange_code !== 'string'
      ) {
        return null;
      }
      return message;
    }
    if (message.type === 'authError') {
      return message;
    }
    return null;
  } catch {
    return null;
  }
};

export const useOfficeAuthDialog = () => {
  const {saveAuth} = useRoamingSettings();
  const [isOpening, setIsOpening] = useState(false);
  const [isExchanging, setIsExchanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<Office.Dialog | null>(null);
  const isHandlingAuthMessageRef = useRef(false);
  const receivedAuthMessageRef = useRef(false);
  const parentOriginRef = useRef(
    typeof window !== 'undefined' ? window.location.origin : ''
  );

  const closeDialog = useCallback((dialog = dialogRef.current) => {
    if (dialogRef.current === dialog) {
      dialogRef.current = null;
    }
    if (dialog) {
      try {
        dialog.close();
      } catch {
        // Dialog may already be closed.
      }
    }
  }, []);

  const processAuthMessage = useCallback(
    (rawMessage: string) => {
      if (isHandlingAuthMessageRef.current) {
        return;
      }

      const message = parseAuthDialogMessage(rawMessage);
      if (!message) {
        let messageType: unknown;
        try {
          messageType = (JSON.parse(rawMessage) as {type?: unknown}).type;
        } catch {
          messageType = undefined;
        }
        reportOutlookAddIn(
          'warning',
          'Outlook add-in received an unusable sign-in message',
          {
            failure: 'auth_message_unusable',
            message_type:
              typeof messageType === 'string' ? messageType : undefined,
            raw_length: rawMessage.length,
          }
        );
        return;
      }
      receivedAuthMessageRef.current = true;
      const dialog = dialogRef.current;

      if (message.type === 'authError') {
        reportOutlookAddIn(
          'error',
          'Outlook add-in sign-in failed in the dialog',
          {
            failure: 'auth_error_from_dialog',
            dialog_error: message.error,
          }
        );
        setError(message.error ?? 'Sign-in failed.');
        closeDialog(dialog);
        return;
      }

      isHandlingAuthMessageRef.current = true;

      void (async () => {
        setIsExchanging(true);
        try {
          const safePodUrl = normalizeLoxoPodUrl(message.pod_url);
          const tokens = await exchangeAuthTokens(
            safePodUrl,
            message.exchange_code
          );
          closeDialog(dialog);
          await saveAuth({
            podUrl: safePodUrl,
            jwt: tokens.access_token,
            refreshToken: tokens.refresh_token,
          });
          setError(null);
          reportOutlookAddIn(
            'info',
            'Outlook add-in finished exchanging the sign-in session',
            {pod_host: new URL(safePodUrl).host}
          );
        } catch (exchangeError) {
          const messageText =
            exchangeError instanceof Error
              ? exchangeError.message
              : 'Could not complete sign-in.';
          reportOutlookAddIn(
            'error',
            'Outlook add-in could not exchange the sign-in session',
            {
              failure: 'token_exchange_failed',
              error_message: messageText,
            }
          );
          setError(messageText);
        } finally {
          isHandlingAuthMessageRef.current = false;
          setIsExchanging(false);
          closeDialog(dialog);
        }
      })();
    },
    [closeDialog, saveAuth]
  );

  const handleAuthDialogMessage = useCallback(
    (arg: DialogMessageReceivedArg) => {
      const rawMessage = dialogMessageArgToString(arg);
      if (!rawMessage) {
        reportOutlookAddIn(
          'warning',
          'Outlook add-in got a sign-in dialog event with no message',
          {
            failure: 'dialog_message_missing',
            office_event_code: 'error' in arg ? arg.error : undefined,
          }
        );
        return;
      }
      processAuthMessage(rawMessage);
    },
    [processAuthMessage]
  );

  useEffect(() => {
    const parentOrigin = window.location.origin;
    parentOriginRef.current = parentOrigin;

    const onWindowMessage = (event: MessageEvent) => {
      if (typeof event.data !== 'string') {
        return;
      }
      if (!isAllowedLoxoPodOrigin(event.origin)) {
        return;
      }
      processAuthMessage(event.data);
    };

    window.addEventListener('message', onWindowMessage);
    return () => {
      window.removeEventListener('message', onWindowMessage);
    };
  }, [processAuthMessage]);

  const openLoginDialog = useCallback(() => {
    if (
      typeof Office === 'undefined' ||
      !Office.context?.ui?.displayDialogAsync
    ) {
      reportOutlookAddIn(
        'error',
        'Outlook add-in cannot open the sign-in dialog',
        {failure: 'display_dialog_unavailable'}
      );
      setError('Office dialog sign-in is not available in this environment.');
      return;
    }

    if (dialogRef.current || isHandlingAuthMessageRef.current) {
      return;
    }

    setError(null);
    setIsOpening(true);
    isHandlingAuthMessageRef.current = false;
    receivedAuthMessageRef.current = false;

    Office.context.ui.displayDialogAsync(
      LOXO_LOGIN_BASE,
      {
        height: 60,
        width: 35,
        displayInIframe: false,
      },
      (asyncResult) => {
        setIsOpening(false);

        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          reportOutlookAddIn(
            'error',
            'Outlook add-in failed to open the sign-in dialog',
            {
              failure: 'display_dialog_failed',
              office_error_code: asyncResult.error?.code,
              office_error_message: asyncResult.error?.message,
            }
          );
          setError('Could not open the Loxo sign-in window.');
          return;
        }

        const dialog = asyncResult.value;
        dialogRef.current = dialog;
        reportOutlookAddIn('info', 'Outlook add-in opened the sign-in dialog');

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          handleAuthDialogMessage
        );

        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (arg: DialogMessageReceivedArg) => {
            if (dialogRef.current === dialog) {
              dialogRef.current = null;
            }
            setIsOpening(false);
            if (!isHandlingAuthMessageRef.current) {
              setIsExchanging(false);
            }
            if (!receivedAuthMessageRef.current) {
              const officeCode = 'error' in arg ? arg.error : undefined;
              reportOutlookAddIn(
                'error',
                'Outlook add-in sign-in dialog closed without a session',
                {
                  failure: 'dialog_closed_without_auth_message',
                  office_event_code: officeCode,
                }
              );
            }
          }
        );
      }
    );
  }, [handleAuthDialogMessage]);

  return {
    openLoginDialog,
    isOpening: isOpening || isExchanging,
    error,
  };
};
